import { PortalPage, PublicPageLayout } from "@fero/layouts";
import {
  ActionLinkPurchaserPurchaseRequestDetailsPage,
  ActionLinkSupplierNewDealRequestsPage,
  AlreadyLoggedInPage,
  CarrierCollectionOrdersPage,
  ForgotPasswordPage,
  HelpUserManualPage,
  IndexRedirect,
  StorageLocProductionOrdersPage as LocProductionOrdersPage,
  LoginPage,
  LoginRedirect,
  LogoutPage,
  NewPurchaserAllocationPage,
  NominatedPurchaserOrderAuthorisationPage,
  OrganisationCompletedDealRenewalsPage,
  OrganisationCreateNewDealRequestPage,
  OrganisationDashboardPage,
  OrganisationDashboardV2Page,
  OrganisationDealExtensionRequestDetailsPage,
  OrganisationExpiringStockPage,
  OrganisationInventoryPage,
  OrganisationNewDealRequestConfirmationPage,
  OrganisationNewDealRequestDeliveryOptionsPage,
  OrganisationNewDealRequestDetailsPage,
  OrganisationNewDealRequestSetupPage,
  OrganisationNewDealRequestsPage,
  OrganisationProductionOrderConfirmationPage,
  OrganisationProductionOrderDetailsPage,
  OrganisationProductionOrderSetupPage,
  OrganisationPurchaseRequestConfirmationPage,
  OrganisationPurchaseRequestDeliveryOptionsPage,
  OrganisationPurchaseRequestDetailsPage,
  OrganisationPurchaseRequestProductionPlanPage,
  OrganisationPurchaseRequestsByPurchaserPage,
  OrganisationPurchaseRequestsPage,
  OrganisationPurchaserRequestSettlementPage,
  OrganisationRepurchaseConfirmationPage,
  OrganisationRepurchaseDeliveryPage,
  OrganisationRepurchaseDetailsPage,
  OrganisationRepurchaseStockPage,
  OrganisationSaleOrdersPage,
  OrganisationTradeSaleSetupPage,
  OrganisationProductionOrdersPage as OrgProductionOrdersPage,
  OrganisationProductSelectionPage as OrgProductionSelectionPage,
  OrgRevaluationRequestDetailsPage,
  OutgoingOrdersDetailPage,
  OutgoingOrdersPage,
  ProductsPage,
  PurchaserDetailsPage,
  PurchaserPurchaseRequestDetailsPage,
  PurchaserPurchaseRequestsPage,
  PurchasersPage,
  QrProductInfoPage,
  ResetPasswordPage,
  RevaluationRequestOverviewPage,
  StockMovementCreatePage,
  StockMovementDashboardPage,
  StockMovementDetailsPage,
  StorageLocIncomingShipmentsPage,
  StorageLocProductBalancesPage,
  StorageLocProductionOrderDetailsPage,
  StorageLocStockAdjustmentConfirmationPage,
  StorageLocStockAdjustmentDetailsPage,
  StorageLocStockAdjustmentSettingsPage,
  StorageLocStockAdjustmentSetupPage,
  StorefrontDashboardPage,
  SupplierNewDealRequestsPage,
} from "@fero/pages";
import { AppRoutes } from "@fero/utils";
import { Stack, Typography } from "@mui/material";
import { QueryClientProvider } from "@tanstack/react-query";
import { makeActionLinkConstants } from "adl-gen/ferovinum/app/api";
import type { GuardedFeature } from "adl-gen/ferovinum/app/db";
import type { UiConfig } from "adl-gen/ferovinum/app/uiconfig";
import type { ApiServices } from "adl-service/api-services";
import { UiConfigContext } from "adl-service/hooks/use-ui-config";
import { LOGIN_BANNER_IMAGE } from "components/assets/images";
import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { type LoginState, useLoginState } from "./identity-state";
import { queryClient } from "./query-client";

export interface LoggedInContextProps {
  loginState?: LoginState;
}

export const LoggedInContext = React.createContext<LoggedInContextProps>({});

interface AppProps {
  /** UI configuration */
  uiconfig: UiConfig;
  createApiServices(token?: string): ApiServices;
}

const {
  SupplierNewDealRequestsRoute,
  PurchaserActionRoute,
  NomPurchaserOrderAuthRoute,
} = makeActionLinkConstants({});

export const App = (props: AppProps) => {
  const [features, setFeatures] = useState<GuardedFeature[]>([]);
  const loginState = useLoginState({
    createApiServices: props.createApiServices,
    setFeatures: (features) => setFeatures(features),
  });

  const PortalPageLayout = useCallback(
    ({ children, ...props }) => {
      return (
        <PortalPage loginState={loginState} {...props}>
          {children}
        </PortalPage>
      );
    },
    [loginState],
  );

  const showNominatedPurchaser = features.includes("nominatedPurchaser");
  const showProductionOrder = features.includes("productionOrder");
  const showStockMovement = features.includes("stockMovement");
  const showStockRevaluation = features.includes("stockRevaluation");
  const showStorefront = features.includes("enable_storefront_ui");
  const showDashboardV2 = features.includes("dashboard_v2");

  const uiConfig = useMemo(() => {
    return {
      ...props.uiconfig,
      features,
    };
  }, [features, props.uiconfig]);

  const loginStateMemo = useMemo(() => ({ loginState }), [loginState]);

  return (
    <Switch>
      {/* Public routes, we don't care if the user is logged in*/}
      <Route path={NomPurchaserOrderAuthRoute}>
        <PublicPageLayout imageURL={LOGIN_BANNER_IMAGE}>
          <NominatedPurchaserOrderAuthorisationPage />
        </PublicPageLayout>
      </Route>

      <UiConfigContext.Provider value={uiConfig}>
        {loginState.userProfileIsLoading ? (
          <div>Loading..</div>
        ) : (
          <LoggedInContext.Provider value={{ loginState }}>
            {loginStateMemo.loginState.user === undefined ? (
              <Switch>
                <Route path={SupplierNewDealRequestsRoute}>
                  <ActionLinkSupplierNewDealRequestsPage />
                </Route>
                <Route path={PurchaserActionRoute}>
                  <ActionLinkPurchaserPurchaseRequestDetailsPage />
                </Route>
                <PublicPageLayout
                  imageURL={LOGIN_BANNER_IMAGE}
                  showMobileLogo={false}
                >
                  <Switch>
                    <Route path={AppRoutes.Login}>
                      <LoginPage {...loginState} />
                    </Route>
                    <Route path={AppRoutes.ForgotPassword}>
                      <ForgotPasswordPage {...loginState} />
                    </Route>
                    <Route exact path={AppRoutes.ResetPassword}>
                      <ResetPasswordPage
                        onResetPassword={loginState.onResetPassword}
                        onLogout={loginState.onLogout}
                        resetPasswordError={loginState.resetPasswordError}
                      />
                    </Route>
                    <Route exact path={AppRoutes.ActivateAccount}>
                      <ResetPasswordPage
                        onResetPassword={loginState.onResetPassword}
                        onLogout={loginState.onLogout}
                        resetPasswordError={loginState.resetPasswordError}
                      />
                    </Route>
                    <Route component={LoginRedirect} />
                  </Switch>
                </PublicPageLayout>
              </Switch>
            ) : (
              <PortalPageLayout>
                <QueryClientProvider client={queryClient}>
                  <Switch>
                    <Route exact path={AppRoutes.ActivateAccount}>
                      <AlreadyLoggedInPage />
                    </Route>
                    <Route exact path={AppRoutes.ResetPassword}>
                      <AlreadyLoggedInPage />
                    </Route>
                    <Route
                      exact
                      path={`${AppRoutes.LocNewDealRequests}/:filter`}
                    >
                      <StorageLocIncomingShipmentsPage />
                    </Route>
                    <Route exact path={AppRoutes.CarrierRequests}>
                      <Redirect
                        to={`${AppRoutes.CarrierRequests}/inProgress`}
                      />
                    </Route>
                    <Route exact path={`${AppRoutes.CarrierRequests}/:filter`}>
                      <CarrierCollectionOrdersPage />
                    </Route>
                    <Route path={`${AppRoutes.SaleOrder}/:saleOrderId`}>
                      <OrganisationRepurchaseDetailsPage />
                    </Route>

                    {/* Org create production order flow */}
                    {showProductionOrder && (
                      <Route
                        path={AppRoutes.OrgProductionOrderProductSelection}
                      >
                        <OrgProductionSelectionPage />
                      </Route>
                    )}
                    {showProductionOrder && (
                      <Route path={AppRoutes.OrgProductionOrderSetup}>
                        <OrganisationProductionOrderSetupPage />
                      </Route>
                    )}
                    {showProductionOrder && (
                      <Route path={AppRoutes.OrgProductionOrderConfirmation}>
                        <OrganisationProductionOrderConfirmationPage />
                      </Route>
                    )}
                    {showProductionOrder && (
                      <Route
                        path={
                          AppRoutes.OrgProductionOrderDetails +
                          "/:productionOrderId"
                        }
                      >
                        <OrganisationProductionOrderDetailsPage />
                      </Route>
                    )}
                    {showProductionOrder && (
                      <Route path={AppRoutes.OrgProductionOrders}>
                        <OrgProductionOrdersPage />
                      </Route>
                    )}

                    <Route exact path={AppRoutes.RepurchaseDelivery}>
                      <OrganisationRepurchaseDeliveryPage />
                    </Route>
                    <Route exact path={AppRoutes.RepurchaseConfirmation}>
                      <OrganisationRepurchaseConfirmationPage />
                    </Route>
                    <Route path={AppRoutes.RepurchaseSaleOrders}>
                      <OrganisationSaleOrdersPage />
                    </Route>
                    <Route path={AppRoutes.OrganisationNewDealRequests}>
                      <OrganisationNewDealRequestsPage />
                    </Route>
                    <Route
                      path={
                        AppRoutes.OrganisationNewDealRequestDetails +
                        "/:purchaseOrderId"
                      }
                    >
                      <OrganisationNewDealRequestDetailsPage />
                    </Route>

                    {/* Org create or edit new deal request flow */}
                    <Route
                      path={`${AppRoutes.CreateNewDealRequest}/:existingNdrId`}
                    >
                      <OrganisationCreateNewDealRequestPage />
                    </Route>
                    <Route path={AppRoutes.CreateNewDealRequest}>
                      <OrganisationCreateNewDealRequestPage />
                    </Route>
                    <Route path={AppRoutes.NewDealRequestSetup}>
                      <OrganisationNewDealRequestSetupPage />
                    </Route>
                    <Route path={AppRoutes.NewDealRequestDeliveryOptions}>
                      <OrganisationNewDealRequestDeliveryOptionsPage />
                    </Route>
                    <Route path={AppRoutes.NewDealRequestConfirmation}>
                      <OrganisationNewDealRequestConfirmationPage />
                    </Route>

                    <Route exact path={AppRoutes.SupplierNewDealRequests}>
                      <Redirect
                        to={`${AppRoutes.SupplierNewDealRequests}/inProgress`}
                      />
                    </Route>
                    <Route exact path={SupplierNewDealRequestsRoute}>
                      <Redirect
                        to={`${AppRoutes.SupplierNewDealRequests}/inProgress`}
                      />
                    </Route>
                    <Route
                      exact
                      path={`${AppRoutes.SupplierNewDealRequests}/:filter`}
                    >
                      <SupplierNewDealRequestsPage />
                    </Route>
                    <Route
                      path={[
                        AppRoutes.LocProductionOrders,
                        AppRoutes.LocExistingProductionOrders,
                      ]}
                    >
                      <LocProductionOrdersPage />
                    </Route>
                    <Route
                      path={
                        AppRoutes.LocProductionOrderDetails +
                        "/:productionOrderId"
                      }
                    >
                      <StorageLocProductionOrderDetailsPage />
                    </Route>
                    <Route path={AppRoutes.Repurchase}>
                      <OrganisationRepurchaseStockPage />
                    </Route>
                    {showDashboardV2 ? (
                      <Route path={AppRoutes.Dashboard}>
                        <OrganisationDashboardV2Page />
                      </Route>
                    ) : (
                      <Route path={AppRoutes.Dashboard}>
                        <OrganisationDashboardPage />
                      </Route>
                    )}
                    <Route path={AppRoutes.ProductBalances}>
                      <StorageLocProductBalancesPage />
                    </Route>
                    <Route exact path={AppRoutes.Inventory}>
                      <OrganisationInventoryPage />
                    </Route>
                    <Route path={`${AppRoutes.Qr}/:hash`}>
                      <QrProductInfoPage />
                    </Route>

                    {showNominatedPurchaser && (
                      <Route
                        exact
                        path={AppRoutes.OrganisationPurchaseRequests}
                      >
                        <OrganisationPurchaseRequestsPage />
                      </Route>
                    )}
                    {showNominatedPurchaser && (
                      <Route
                        exact
                        path={AppRoutes.OrganisationCreatePurchaseRequest}
                      >
                        <OrganisationTradeSaleSetupPage />
                      </Route>
                    )}
                    {showNominatedPurchaser && showProductionOrder && (
                      <Route
                        path={
                          AppRoutes.OrganisationPurchaseRequestProductionPlan
                        }
                      >
                        <OrganisationPurchaseRequestProductionPlanPage />
                      </Route>
                    )}
                    {showNominatedPurchaser && (
                      <Route
                        exact
                        path={
                          AppRoutes.OrganisationPurchaseRequestDeliveryOptions
                        }
                      >
                        <OrganisationPurchaseRequestDeliveryOptionsPage />
                      </Route>
                    )}
                    {showNominatedPurchaser && (
                      <Route
                        exact
                        path={AppRoutes.OrganisationPurchaseRequestSettlement}
                      >
                        <OrganisationPurchaserRequestSettlementPage />
                      </Route>
                    )}
                    {showNominatedPurchaser && (
                      <Route
                        exact
                        path={AppRoutes.OrganisationPurchaseRequestConfirmation}
                      >
                        <OrganisationPurchaseRequestConfirmationPage />
                      </Route>
                    )}
                    {showNominatedPurchaser && (
                      <Route
                        path={
                          AppRoutes.OrganisationPurchaseRequestDetails +
                          "/:purchaseRequestId"
                        }
                      >
                        <OrganisationPurchaseRequestDetailsPage />
                      </Route>
                    )}
                    {showNominatedPurchaser && (
                      <Route
                        exact
                        path={AppRoutes.OrganisationPurchaseRequestByPurchaser}
                      >
                        <OrganisationPurchaseRequestsByPurchaserPage />
                      </Route>
                    )}
                    {showNominatedPurchaser && (
                      <Route
                        exact
                        path={AppRoutes.OrganisationPurchaseRequestPurchasers}
                        component={PurchasersPage}
                      />
                    )}
                    {showNominatedPurchaser && (
                      <Route
                        path={`${AppRoutes.OrganisationPurchaseRequestPurchasers}/:nominatedPurchaserId`}
                        component={PurchaserDetailsPage}
                      />
                    )}
                    {showNominatedPurchaser && (
                      <Route
                        exact
                        path={AppRoutes.OrganisationPurchaseRequestProducts}
                        component={ProductsPage}
                      />
                    )}

                    <Route exact path={AppRoutes.StorageLocationOutgoingOrders}>
                      <Redirect
                        to={`${AppRoutes.StorageLocationOutgoingOrders}/outstanding`}
                      />
                    </Route>
                    <Route
                      path={
                        AppRoutes.StorageLocationOutgoingOrderDetails +
                        "/:purchaseRequestId"
                      }
                    >
                      <OutgoingOrdersDetailPage />
                    </Route>
                    <Route
                      path={`${AppRoutes.StorageLocationOutgoingOrders}/:filter`}
                    >
                      <OutgoingOrdersPage />
                    </Route>

                    {/* Purchaser pages */}
                    <Route exact path={AppRoutes.PurchaserPurchaseRequests}>
                      <PurchaserPurchaseRequestsPage />
                    </Route>
                    <Route
                      exact
                      path={`${PurchaserActionRoute}/:purchaseRequestId`}
                      render={(props) => (
                        <Redirect
                          to={`${AppRoutes.PurchaserPurchaseRequestDetails}/${props.match.params.purchaseRequestId}`}
                        />
                      )}
                    />
                    <Route
                      path={
                        AppRoutes.PurchaserPurchaseRequestDetails +
                        "/:purchaseRequestId"
                      }
                    >
                      <PurchaserPurchaseRequestDetailsPage />
                    </Route>

                    {/* Stock adjustment pages */}
                    <Route
                      path={`${AppRoutes.StorageLocStockAdjustmentSetup}/:variant`}
                    >
                      <StorageLocStockAdjustmentSetupPage />
                    </Route>
                    <Route
                      exact
                      path={AppRoutes.StorageLocStockAdjustmentSettings}
                    >
                      <StorageLocStockAdjustmentSettingsPage />
                    </Route>
                    <Route
                      exact
                      path={AppRoutes.StorageLocStockAdjustmentConfirmation}
                    >
                      <StorageLocStockAdjustmentConfirmationPage />
                    </Route>
                    <Route
                      exact
                      path={
                        AppRoutes.StorageLocStockAdjustmentDetails +
                        "/:variant/:stockAdjustmentId"
                      }
                    >
                      <StorageLocStockAdjustmentDetailsPage />
                    </Route>

                    {/* Expiring stock pages */}
                    <Route exact path={AppRoutes.OrganisationExpiringStock}>
                      <OrganisationExpiringStockPage />
                    </Route>
                    <Route
                      exact
                      path={
                        AppRoutes.OrganisationCompletedDealExtensionRequests
                      }
                    >
                      <OrganisationCompletedDealRenewalsPage />
                    </Route>
                    <Route
                      path={
                        AppRoutes.OrganisationDealExtensionRequestDetails +
                        "/:dealExtensionRequestId"
                      }
                    >
                      <OrganisationDealExtensionRequestDetailsPage />
                    </Route>

                    {/* Stock Movement pages */}
                    {showStockMovement && (
                      <Route exact path={AppRoutes.StockMovement}>
                        <StockMovementDashboardPage />
                      </Route>
                    )}
                    {showStockMovement && (
                      <Route exact path={AppRoutes.CreateStockMovement}>
                        <StockMovementCreatePage />
                      </Route>
                    )}
                    {showStockMovement && (
                      <Route
                        exact
                        path={`${AppRoutes.StockMovementDetails}/:stockMovementId`}
                      >
                        <StockMovementDetailsPage />
                      </Route>
                    )}
                    {/* Stock Revaluation pages */}
                    {showStockRevaluation && (
                      <Route exact path={AppRoutes.StockRevaluation}>
                        <RevaluationRequestOverviewPage />
                      </Route>
                    )}
                    {showStockRevaluation && (
                      <Route
                        exact
                        path={
                          AppRoutes.StockRevaluationDetails +
                          "/:revaluationRequestId"
                        }
                      >
                        <OrgRevaluationRequestDetailsPage />
                      </Route>
                    )}

                    {/* Storefront pages */}
                    {showStorefront && (
                      <Route exact path={AppRoutes.Storefront}>
                        <StorefrontDashboardPage />
                      </Route>
                    )}
                    {showStorefront && (
                      <Route exact path={AppRoutes.NewPurchaserAllocation}>
                        <NewPurchaserAllocationPage />
                      </Route>
                    )}

                    <Route exact path={AppRoutes.Index}>
                      <IndexRedirect />
                    </Route>
                    <Route exact path={[AppRoutes.Logout, AppRoutes.Login]}>
                      <LogoutPage loginState={loginState} />
                    </Route>

                    <Route exact path={[AppRoutes.HelpUserManual]}>
                      <HelpUserManualPage />
                    </Route>

                    <Route>
                      <Stack>
                        <Typography variant="h2">Page Not Found</Typography>
                      </Stack>
                    </Route>
                  </Switch>
                </QueryClientProvider>
              </PortalPageLayout>
            )}
          </LoggedInContext.Provider>
        )}
      </UiConfigContext.Provider>
    </Switch>
  );
};
